// src/components/CustomerSearch.tsx
import React, { useState, useEffect } from 'react';
import { mockDataService, Customer } from '../services/MockDataService';

interface CustomerSearchProps {
  onCustomerSelect?: (customer: Customer) => void;
}

const CustomerSearch: React.FC<CustomerSearchProps> = ({ onCustomerSelect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Search customers when query changes
  useEffect(() => {
    const searchCustomers = async () => {
      setLoading(true);
      try {
        const results = await mockDataService.searchCustomers(searchQuery);
        setCustomers(results);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setLoading(false);
      }
    };

    searchCustomers();
  }, [searchQuery]);

  const handleCustomerClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    onCustomerSelect?.(customer);
  };

  const getStatusBadge = (status: Customer['customer_status']) => {
    const styles = {
      active: 'bg-green-100 text-green-800 border-green-200',
      pending_terms: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      pending_consent: 'bg-orange-100 text-orange-800 border-orange-200',
      unsubscribed: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${styles[status]}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button 
              onClick={() => window.history.back()}
              className="mr-4 text-gray-500 hover:text-gray-700"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              🔍 Customer Search
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search by phone number or status
              </label>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="e.g. +1234567890 or active"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {loading && (
              <div className="flex items-center text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                Searching...
              </div>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Search Results ({customers.length})
            </h3>
          </div>

          {customers.length === 0 && !loading ? (
            <div className="p-8 text-center">
              <span className="text-4xl mb-4 block">📭</span>
              <p className="text-gray-500">
                {searchQuery ? 'No customers found matching your search.' : 'Start typing to search customers...'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {customers.map((customer) => (
                <div
                  key={customer.phone_number}
                  onClick={() => handleCustomerClick(customer)}
                  className={`p-6 hover:bg-gray-50 cursor-pointer transition-colors ${
                    selectedCustomer?.phone_number === customer.phone_number ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h4 className="text-lg font-medium text-gray-900">
                          {customer.phone_number}
                        </h4>
                        {getStatusBadge(customer.customer_status)}
                      </div>
                      
                      <div className="mt-2 space-y-1">
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">First seen:</span> {formatDate(customer.first_seen_at)}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Channel:</span> {customer.initial_channel.toUpperCase()}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Active tickets:</span> {customer.active_ticket_ids.length}
                        </p>
                        {customer.extracted_info.zip_code && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">ZIP:</span> {customer.extracted_info.zip_code}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col items-end space-y-2">
                      <div className="flex space-x-2">
                        {customer.sms_opted_in && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            📱 SMS
                          </span>
                        )}
                        {customer.terms_accepted && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✅ Terms
                          </span>
                        )}
                      </div>
                      
                      <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                        View Details →
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected Customer Quick Info */}
        {selectedCustomer && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-lg font-medium text-blue-900 mb-3">
              Selected: {selectedCustomer.phone_number}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="font-medium text-blue-800">Status:</span>
                <div className="mt-1">{getStatusBadge(selectedCustomer.customer_status)}</div>
              </div>
              <div>
                <span className="font-medium text-blue-800">Conversations:</span>
                <p className="text-blue-700">{selectedCustomer.conversation_ids.length}</p>
              </div>
              <div>
                <span className="font-medium text-blue-800">Active Tickets:</span>
                <p className="text-blue-700">{selectedCustomer.active_ticket_ids.length}</p>
              </div>
            </div>
            <div className="mt-4 flex space-x-3">
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium">
                View Full Profile
              </button>
              <button className="bg-white hover:bg-gray-50 text-blue-600 border border-blue-600 px-4 py-2 rounded-md text-sm font-medium">
                View Events
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default CustomerSearch;